import { ThemeEvents } from '@theme/events';

/**
 * Add to cart sticky (estilo Scuffers), con encaje elemento a elemento ligado al scroll.
 *
 * - Al cargar la PDP la barra aparece directamente en su sitio (sin animación de entrada).
 * - Contiene copias vivas de los bloques reales de la ficha: estrellas (review), título (el
 *   bloque de texto que muestra el nombre web o el marcado con «Mostrar en el add to cart
 *   sticky»), selector de variantes, precio y botón. Cada copia conserva la configuración
 *   del bloque original en el editor de temas.
 * - Debajo del botón, siempre, una copia simplificada del bloque de entrega estimada
 *   (shipping-status-block.liquid): solo «Recíbelo el <fecha>.», sin el «+». Es fija: no
 *   participa en el encaje y su original nunca se oculta.
 * - Regla general: mientras la copia de un elemento se ve en la barra, su original está
 *   oculto. Así nunca pueden verse los dos a la vez.
 *
 * Primera bajada (desde la carga y mientras solo se baje), todo al ritmo del scroll:
 *   1. Fija: la copia está quieta en la barra mientras su original sube desde abajo.
 *   2. Desliz: en los últimos píxeles antes de que el original llegue a su altura, se desplaza
 *      solo en horizontal hasta la columna del original.
 *   3. Encajada: el original ha llegado; la copia ya está en su sitio y se mueve con la
 *      página igual que él. El fondo blanco, que seguía a su altura, se retira poco a poco con
 *      el scroll hacia el siguiente elemento.
 *   4. Liberada: cuando el fondo ya ha dejado al descubierto el sitio del original, la copia
 *      se cambia por el original sin que se note.
 *   El botón es el último y no se mueve: el botón real sube hasta él mientras el fondo se
 *   funde.
 *
 * En cuanto se sube (o tras encajar el botón) ya no hay desplazamientos ligados al scroll:
 * cada elemento aparece en la barra —con un fundido suave— cuando la barra tapa la mayor
 * parte de su original (COVER_RATIO), y desaparece cuando su original vuelve a asomar. Al subir
 * aparece primero el botón y luego, de forma escalonada, el precio, el selector, el título y
 * las estrellas. Si se pasa el botón por arriba, la barra completa aparece deslizando en su
 * versión compacta (atributo data-compact: borde recto con sombra y precio más pequeño).
 *
 * - Los clics en las copias se reenvían a los elementos originales.
 */

const SLOT_KEYS = ['rating', 'title', 'variants', 'price', 'button'];
/** Huecos fijos: se copian y se ven siempre con la barra, sin encaje ni ocultar el original. */
const STATIC_KEYS = ['shipping'];
/** Texto que precede a la fecha de entrega en la barra (se puede cambiar con data-shipping-prefix). */
const SHIPPING_PREFIX = 'Recíbelo el';
/** Píxeles de scroll (antes de llegar) durante los que una copia se desliza en horizontal. */
const TRAVEL_X = 80;
/** Píxeles de scroll durante los que el fondo se retira hacia el siguiente elemento. */
const RETREAT = 60;
/** Píxeles de scroll durante los que el fondo se funde mientras el botón real se acerca. */
const BUTTON_FADE = 100;
/** Alto del degradado del borde superior del fondo (debe coincidir con el CSS). */
const FADE_EDGE = 16;
/** Parte del original que la barra debe tapar para que su copia aparezca (al subir). */
const COVER_RATIO = 0.8;
/** Píxeles de subida a partir de los que se da por terminada la primera bajada. */
const SCROLL_UP_THRESHOLD = 8;
/** Aparición de los elementos en la barra (modo sin desplazamientos). */
const APPEAR = { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' };
/** Marca en el original mientras su copia se ve en la barra (lo oculta por CSS). */
const COVERED_ATTR = 'data-sticky-flying';
/** Atributos de estado de las animaciones del botón (blocks/add-to-cart.liquid): no son cambios de contenido. */
const ANIMATION_ATTR_PREFIX = 'data-atc-anim';
const ANIMATION_STATE_ATTRS = ['data-atc-anim-run', 'data-atc-anim-reset', 'data-atc-anim-pop'];
/** Reintentos para localizar bloques que aún no se han pintado al arrancar. */
const MAX_DISCOVER_RETRIES = 12;
const MOBILE_QUERY = '(max-width: 749px)';

/** @param {string | null | undefined} text */
const normalize = (text) => (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

/** @param {number} value */
const clamp01 = (value) => Math.min(1, Math.max(0, value));

/**
 * @param {number} from
 * @param {number} to
 * @param {number} t
 */
const lerp = (from, to, t) => from + (to - from) * t;

/**
 * @typedef {'fixed' | 'slide' | 'docked' | 'released'} Phase
 * @typedef {{
 *   key: string,
 *   slot: HTMLElement,
 *   clone: HTMLElement | null,
 *   src: HTMLElement | undefined,
 *   restTop: number,
 *   restLeft: number,
 *   phase: Phase,
 *   gap: number,
 *   target: DOMRect | null,
 * }} Item
 */

class StickyAddToCart extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();

  /** @type {Record<string, HTMLElement>} */
  #sources = {};

  /** @type {MutationObserver[]} */
  #observers = [];

  /** @type {Set<string>} */
  #pendingSync = new Set();

  #barVisible = false;
  /** Si la barra ya se ha mostrado alguna vez (la primera aparición, al cargar, es sin animación). */
  #everShown = false;
  #buttonWasPast = false;
  /** Tras la primera bajada los elementos solo aparecen/desaparecen, sin desplazarse. */
  #simpleMode = false;
  /** Scroll máximo alcanzado durante la primera bajada (para detectar que se sube). */
  #maxScroll = 0;
  /** Qué huecos estaban visibles en la barra en el modo sin desplazamientos. @type {Map<string, boolean>} */
  #shown = new Map();
  /** Desplazamiento que tenía cada copia al terminar la primera bajada (vuelve a su sitio suave). @type {Map<string, string>} */
  #settle = new Map();
  /** Borde superior del fondo en el modo sin desplazamientos (px dentro de la barra). */
  #simpleBgTop = -1;
  #ticking = false;
  #discoverRetries = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retryTimer;

  /** @type {HTMLButtonElement | null} */
  #realButton = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLElement | null} */
  #background = null;

  connectedCallback() {
    // Nuevo controlador en cada conexión (el editor de temas desconecta y reconecta secciones)
    this.#abort = new AbortController();
    this.#discoverRetries = 0;

    this.#bar = this.querySelector('.sticky-add-to-cart__bar');
    if (!this.#bar) return;

    this.#background = this.#bar.querySelector('.sticky-add-to-cart__bg');
    // En la primera bajada el fondo sigue al scroll: sin transiciones por tiempo
    if (this.#background) this.#background.style.transition = 'none';

    // Esperamos a que el resto de bloques hayan pintado y ejecutado sus scripts
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true, signal: this.#abort.signal });
    } else {
      requestAnimationFrame(() => this.#init());
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    clearTimeout(this.#retryTimer);
    this.#disconnectObservers();
    for (const src of Object.values(this.#sources)) src.removeAttribute(COVERED_ATTR);
  }

  #init() {
    const { signal } = this.#abort;
    const requestUpdate = () => this.#requestUpdate();

    window.addEventListener('scroll', requestUpdate, { passive: true, signal });
    window.addEventListener(
      'resize',
      () => {
        this.#alignPrice();
        requestUpdate();
      },
      { signal }
    );
    window.addEventListener('load', () => this.#refresh(), { once: true, signal });
    this.#bar?.addEventListener('click', this.#onClick, { signal });

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, () => this.#refresh(), { signal });

    this.#maxScroll = window.scrollY;
    this.#discover();
    this.#update();
  }

  // ───────────────────────── Localizar bloques originales ─────────────────────────

  #discover() {
    this.#disconnectObservers();
    for (const src of Object.values(this.#sources)) src.removeAttribute(COVERED_ATTR);
    this.#sources = {};

    const scope = this.closest('.shopify-section') ?? document;
    const productId = this.dataset.productId;

    /** @param {Element} el */
    const usable = (el) => !this.contains(el) && el instanceof HTMLElement && el.getClientRects().length > 0;

    /** @param {string} selector */
    const find = (selector) =>
      /** @type {HTMLElement | undefined} */ (Array.from(scope.querySelectorAll(selector)).find(usable));

    const form =
      find(`product-form-component[data-product-id="${productId}"]`) ?? find('product-form-component');
    this.#realButton = /** @type {HTMLButtonElement | null} */ (
      form?.querySelector('[ref="addToCartButton"]') ?? null
    );

    if (this.#realButton) {
      // El bloque add-to-cart.liquid envuelve el botón con sus variables de estilo
      this.#sources.button =
        /** @type {HTMLElement} */ (this.#realButton.closest('[style*="--add-to-cart-border-radius"]')) ??
        this.#realButton;
    }

    if (this.#slot('rating')) {
      const el = find('[data-sticky-atc="rating"]') ?? find('[data-review-stars]');
      if (el) this.#sources.rating = el;
    }

    if (this.#slot('title')) {
      // 1º el bloque de texto marcado; 2º el bloque de texto cuyo contenido es el nombre web
      const wanted = normalize(this.dataset.titleText);
      const el =
        find('[data-sticky-atc="title"]') ??
        /** @type {HTMLElement | undefined} */ (
          wanted
            ? Array.from(scope.querySelectorAll('[id^="text-block-"]')).find(
                (block) =>
                  usable(block) &&
                  normalize((block.querySelector('.text-block__text') ?? block).textContent) === wanted
              )
            : undefined
        );
      if (el) this.#sources.title = el;
    }

    if (this.#slot('variants')) {
      const el =
        find('[data-sticky-atc="variants"]') ??
        find('.shopify-block[id*="product_variant_selectors"]') ??
        find('variant-picker');
      if (el) this.#sources.variants = el;
    }

    if (this.#slot('price')) {
      const el = find('[data-sticky-atc="price"]') ?? find('product-price[data-block-id]') ?? find('product-price');
      if (el) this.#sources.price = el;
    }

    if (this.#slot('shipping')) {
      // Bloque «Compra ahora recíbelo el…» (shipping-status-block.liquid)
      const el = find('[data-sticky-atc="shipping"]') ?? find('.delivery-estimate');
      if (el) this.#sources.shipping = el;
    }

    for (const key of [...SLOT_KEYS, ...STATIC_KEYS]) {
      const src = this.#sources[key];
      try {
        this.#sync(key);
      } catch (error) {
        console.warn('[sticky-add-to-cart] No se pudo copiar el bloque', key, error);
      }
      if (!src) continue;

      src.setAttribute('data-sticky-src', key);
      const observer = new MutationObserver((records) => {
        // Ocultar el original mientras su copia se ve, o el estado de las animaciones del botón,
        // no son cambios de contenido: no hace falta rehacer la copia
        const ignorable = records.every(
          (r) =>
            r.type === 'attributes' &&
            (r.attributeName === COVERED_ATTR || Boolean(r.attributeName?.startsWith(ANIMATION_ATTR_PREFIX)))
        );
        if (ignorable) return;
        this.#queueSync(key);
      });
      observer.observe(src, { subtree: true, childList: true, attributes: true, characterData: true });
      this.#observers.push(observer);
    }

    // Si algún bloque aún no se ha pintado (carga diferida, fuentes…), se vuelve a intentar
    const missing =
      !this.#realButton ||
      SLOT_KEYS.some((key) => key !== 'title' && this.#slot(key) && !this.#sources[key]);
    clearTimeout(this.#retryTimer);
    if (missing && this.#discoverRetries < MAX_DISCOVER_RETRIES) {
      this.#retryTimer = setTimeout(() => {
        this.#discoverRetries++;
        this.#discover();
        this.#update();
      }, 400);
    }
  }

  #disconnectObservers() {
    this.#observers.forEach((o) => o.disconnect());
    this.#observers = [];
  }

  #refresh() {
    // Tras un cambio de variante el tema puede re-renderizar los bloques
    requestAnimationFrame(() => {
      this.#discover();
      this.#update();
    });
  }

  // ───────────────────────── Copias de los bloques ─────────────────────────

  /** @param {string} key */
  #slot(key) {
    return /** @type {HTMLElement | null} */ (this.#bar?.querySelector(`[data-slot="${key}"]`) ?? null);
  }

  /** @param {string} key */
  #queueSync(key) {
    if (this.#pendingSync.has(key)) return;
    this.#pendingSync.add(key);
    requestAnimationFrame(() => {
      this.#pendingSync.delete(key);
      this.#sync(key);
      // La copia nueva tiene que recuperar su posición
      this.#update();
    });
  }

  /** @param {string} key */
  #sync(key) {
    const slot = this.#slot(key);
    if (!slot) return;

    const src = this.#sources[key];
    if (!src) {
      // Sin bloque original: se usa el contenido de respaldo (p. ej. el nombre web) si lo hay
      slot.hidden = !slot.querySelector('[data-fallback]');
      return;
    }

    const clone = /** @type {HTMLElement} */ (src.cloneNode(true));
    this.#sanitize(clone, key);

    // Estado de formularios que no se refleja en atributos
    const srcFields = src.querySelectorAll('select, input');
    const cloneFields = clone.querySelectorAll('select, input');
    srcFields.forEach((field, i) => {
      const twin = /** @type {HTMLInputElement | HTMLSelectElement | undefined} */ (cloneFields[i]);
      if (!twin) return;
      twin.value = /** @type {HTMLInputElement} */ (field).value;
      if (field instanceof HTMLInputElement && twin instanceof HTMLInputElement) twin.checked = field.checked;
    });

    slot.replaceChildren(clone);
    slot.hidden = false;
    if (key === 'price') this.#alignPrice();
  }

  /**
   * En móvil el precio comparte fila con el selector: se sube lo que mide su margen interior
   * superior para que la parte de arriba del precio quede alineada con la del selector, sin
   * cambiar la separación nombre→selector ni precio→botón.
   */
  #alignPrice() {
    const slot = this.#slot('price');
    const clone = /** @type {HTMLElement | null} */ (slot?.querySelector('[data-sticky-clone]') ?? null);
    if (!slot) return;
    const padding =
      clone && window.matchMedia(MOBILE_QUERY).matches ? parseFloat(getComputedStyle(clone).paddingTop) || 0 : 0;
    slot.style.marginTop = padding ? `${-padding}px` : '';
  }

  /**
   * Entrega estimada en la barra: sin el «+» ni su desplegable, y con el texto «Recíbelo el»
   * delante de la fecha (en lugar de «Compra ahora recíbelo el»).
   * @param {HTMLElement} root
   */
  #simplifyShipping(root) {
    root.querySelectorAll('.delivery-plus, .delivery-estimate__extra').forEach((el) => el.remove());
    root.removeAttribute('data-expanded');

    const text = root.querySelector('.delivery-estimate__row .delivery-text');
    const date = text?.querySelector('strong');
    if (!text || !date) return;
    for (const node of Array.from(text.childNodes)) {
      if (node === date) break;
      node.remove();
    }
    text.insertBefore(document.createTextNode(`${this.dataset.shippingPrefix || SHIPPING_PREFIX} `), date);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} key
   */
  #sanitize(root, key) {
    if (key === 'button') {
      // El botón vive dentro de componentes del tema (add-to-cart-component…) que exigen
      // referencias internas: en la copia se convierten en simples <span> con sus atributos.
      const components = Array.from(root.querySelectorAll('*')).filter((el) => el.localName.includes('-'));
      for (const el of components.reverse()) {
        const plain = document.createElement('span');
        for (const attr of Array.from(el.attributes)) plain.setAttribute(attr.name, attr.value);
        plain.append(...Array.from(el.childNodes));
        el.replaceWith(plain);
      }
    }

    if (key === 'shipping') this.#simplifyShipping(root);

    const nodes = [root, ...root.querySelectorAll('*')];

    for (const el of nodes) {
      if (el.tagName === 'SCRIPT') {
        // Se sustituye (no se borra) para que los índices coincidan con el original
        el.replaceWith(document.createElement('template'));
        continue;
      }

      el.removeAttribute('data-sticky-src');
      el.removeAttribute(COVERED_ATTR);
      el.removeAttribute('data-shopify-editor-block');
      el.removeAttribute('ref');
      // La copia empieza sin ninguna animación del botón a medias
      for (const attr of ANIMATION_STATE_ATTRS) el.removeAttribute(attr);
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith('on:')) el.removeAttribute(attr.name);
      }

      if (key === 'button' || el.matches('input, button, select, textarea')) {
        el.removeAttribute('id');
        el.removeAttribute('form');
        if (key === 'button') el.removeAttribute('name');
      }
    }

    if (key === 'button') {
      root.classList.remove('hide-on-mobile');
      const button = root.matches('button') ? root : root.querySelector('button');
      if (button) {
        button.setAttribute('type', 'button');
        button.removeAttribute('data-puppet');
      }
    }

    root.style.transform = '';
    root.setAttribute('data-sticky-clone', key);
  }

  // ───────────────────────── Clics → originales ─────────────────────────

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const slot = /** @type {HTMLElement | null} */ (target.closest('[data-slot]'));
    if (!slot) return;

    const key = slot.dataset.slot ?? '';

    if (key === 'button') {
      event.preventDefault();
      if (this.#realButton && !this.#realButton.disabled) this.#realButton.click();
      return;
    }

    // La entrega estimada de la barra es solo informativa
    if (key === 'shipping') return;

    // Los controles nativos (select, enlaces…) ya funcionan solos en la copia
    if (target.closest('a[href], select, input, label, textarea')) return;

    const src = this.#sources[key];
    const cloneRoot = /** @type {HTMLElement | null} */ (slot.querySelector('[data-sticky-clone]'));
    if (!src || !cloneRoot) return;

    const twin = this.#twin(cloneRoot, src, target);
    if (twin instanceof HTMLElement) twin.click();
  };

  /**
   * Devuelve el elemento del original que ocupa la misma posición que `target` en la copia.
   * @param {Element} cloneRoot
   * @param {Element} srcRoot
   * @param {Element} target
   */
  #twin(cloneRoot, srcRoot, target) {
    /** @type {number[]} */
    const path = [];
    let el = target;
    while (el && el !== cloneRoot) {
      const parent = el.parentElement;
      if (!parent) return null;
      path.unshift(Array.prototype.indexOf.call(parent.children, el));
      el = parent;
    }
    if (el !== cloneRoot) return null;

    let twin = srcRoot;
    for (const index of path) {
      const next = twin.children[index];
      if (!next) return twin;
      twin = next;
    }
    return twin;
  }

  // ───────────────────────── Posiciones ─────────────────────────

  /**
   * Posición de un elemento respecto a la barra (sin transformaciones).
   * @param {HTMLElement} element
   */
  #offsetInBar(element) {
    let top = 0;
    let left = 0;
    /** @type {HTMLElement | null} */
    let el = element;
    while (el && el !== this.#bar) {
      top += el.offsetTop;
      left += el.offsetLeft;
      el = /** @type {HTMLElement | null} */ (el.offsetParent);
    }
    return { top, left };
  }

  /** Esquina superior izquierda (viewport) de la barra en reposo y visible. */
  #barRestOrigin() {
    const bar = /** @type {HTMLElement} */ (this.#bar);
    const styles = getComputedStyle(bar);
    const bottom = parseFloat(styles.bottom) || 0;
    const top = window.innerHeight - bottom - bar.offsetHeight;
    // En horizontal la barra no se desplaza: su posición actual vale
    const left = bar.getBoundingClientRect().left;
    return { top, left };
  }

  // ───────────────────────── Estado ligado al scroll ─────────────────────────

  #requestUpdate() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  }

  #update() {
    const button = this.#realButton;
    const bar = this.#bar;
    if (!button || !bar) return;
    if (!button.isConnected) {
      this.#discover();
      return;
    }

    // La primera bajada termina en cuanto se sube un poco: desde ahí, sin desplazamientos
    const scroll = window.scrollY;
    if (!this.#simpleMode) {
      if (scroll < this.#maxScroll - SCROLL_UP_THRESHOLD) this.#enterSimpleMode();
      else this.#maxScroll = Math.max(this.#maxScroll, scroll);
    }

    const viewport = window.innerHeight;

    const buttonRect = button.getBoundingClientRect();
    // El botón real ya pasó por arriba
    const buttonPast = buttonRect.height > 0 && buttonRect.bottom <= 0;

    // Versión compacta cuando la barra reaparece tras pasar los botones: borde recto con sombra
    // y precio más pequeño (CSS). Se aplica antes de medir porque cambia la altura de la barra.
    const compact = this.#simpleMode && buttonPast;
    if (bar.hasAttribute('data-compact') !== compact) bar.toggleAttribute('data-compact', compact);

    const origin = this.#barRestOrigin();
    const paddingTop = parseFloat(getComputedStyle(bar).paddingTop) || 0;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    const footerHit = Boolean(footer && footer.getBoundingClientRect().top < viewport - 50);

    /** @type {Item[]} */
    const items = SLOT_KEYS.map((key) => ({ key, slot: this.#slot(key) }))
      .filter(({ slot }) => slot && !slot.hidden)
      .map(({ key, slot }) => {
        const el = /** @type {HTMLElement} */ (slot);
        const clone = /** @type {HTMLElement | null} */ (el.querySelector('[data-sticky-clone]'));
        const base = this.#offsetInBar(clone ?? el);
        const src = this.#sources[key];
        const target = src && src.getClientRects().length > 0 ? src.getBoundingClientRect() : null;
        return {
          key,
          slot: el,
          clone,
          src,
          restTop: origin.top + base.top,
          restLeft: origin.left + base.left,
          phase: /** @type {Phase} */ ('fixed'),
          gap: target ? target.top - (origin.top + base.top) : Infinity,
          target,
        };
      });

    const buttonItem = items.find((item) => item.key === 'button');
    const buttonGap = buttonItem ? buttonRect.top - buttonItem.restTop : Infinity;
    // El botón está en su sitio cuando el botón real llega a la altura del botón del sticky
    const buttonDocked = !buttonPast && buttonRect.height > 0 && buttonGap <= 0;

    if (this.#simpleMode) {
      this.#updateSimple(items, { buttonPast, buttonDocked, footerHit, origin, paddingTop, viewport });
    } else {
      this.#updateFirstPass(items, { buttonPast, buttonDocked, buttonGap, footerHit, origin, paddingTop, viewport });
      // Primer encaje completo: a partir de aquí, sin desplazamientos
      if (buttonDocked) this.#enterSimpleMode();
    }

    this.#buttonWasPast = buttonPast;
  }

  #enterSimpleMode() {
    if (this.#simpleMode) return;
    this.#simpleMode = true;
    // Se parte de lo que se ve ahora mismo en la barra para no animar lo que ya estaba, y se
    // recuerda el desplazamiento de cada copia para devolverla a su sitio con suavidad
    for (const key of SLOT_KEYS) {
      const slot = this.#slot(key);
      const visible = Boolean(slot && !slot.hidden && slot.dataset.docked !== 'true' && this.#barVisible);
      this.#shown.set(key, visible);
      const transform = /** @type {HTMLElement | null} */ (slot?.querySelector('[data-sticky-clone]') ?? null)
        ?.style.transform;
      if (visible && transform) this.#settle.set(key, transform);
    }
    this.#simpleBgTop = parseFloat(this.#bar?.style.getPropertyValue('--sticky-atc-bg-top') || '-1');
  }

  /**
   * Primera bajada: copias que se deslizan en horizontal, encajan y se cambian por el original.
   * @param {Item[]} items
   * @param {{ buttonPast: boolean, buttonDocked: boolean, buttonGap: number, footerHit: boolean,
   *   origin: { top: number, left: number }, paddingTop: number, viewport: number }} state
   */
  #updateFirstPass(items, { buttonPast, buttonDocked, buttonGap, footerHit, origin, paddingTop, viewport }) {
    const bar = /** @type {HTMLElement} */ (this.#bar);

    // 1) Fase de cada elemento según lo que le falta a su original para llegar a su altura
    for (const item of items) {
      if (item.key === 'button') {
        item.phase = buttonDocked ? 'released' : 'fixed';
        continue;
      }
      if (buttonPast) continue;
      if (buttonDocked) {
        item.phase = 'released';
        continue;
      }
      if (!item.target || !item.clone) continue;
      if (item.gap <= 0) item.phase = 'docked';
      else if (item.gap < TRAVEL_X) item.phase = 'slide';
    }

    // 2) Fondo blanco: cubre desde el primer elemento que aún no ha llegado. Tras cada encaje
    //    se queda a la altura de ese elemento y se retira hacia el siguiente al ritmo del scroll
    const pending = items.filter((item) => item.phase === 'fixed' || item.phase === 'slide');
    const pendingTop = pending.length ? Math.min(...pending.map((item) => item.restTop)) - paddingTop : viewport;
    let bgTop = pendingTop;
    for (const item of items) {
      if (item.key === 'button' || !Number.isFinite(item.gap) || item.gap > 0) continue;
      const t = clamp01(-item.gap / RETREAT);
      bgTop = Math.min(bgTop, lerp(item.restTop - paddingTop, pendingTop, t));
    }

    // 3) Una copia encajada se cambia por su original cuando el fondo ya lo ha descubierto
    for (const item of items) {
      if (item.phase === 'docked' && item.target && item.target.bottom <= bgTop) item.phase = 'released';
    }

    const active = items.filter((item) => item.phase !== 'released');
    const want = !footerHit && active.length > 0;
    if (want !== this.#barVisible) {
      // La primera aparición (al cargar) es sin animación. Después, desaparece sin deslizar
      // cuando todo ha encajado bajando, y desliza si es por el footer o por pasar el botón.
      const firstShow = want && !this.#everShown;
      this.#setBarVisible(want, firstShow || (!want && !footerHit && !this.#buttonWasPast));
    }

    // 4) Aplicar posiciones. Mientras una copia se ve, su original está oculto (sin duplicados)
    for (const item of items) {
      const { clone, target, phase } = item;
      let transform = '';

      if (this.#barVisible && clone && target) {
        const dx = target.left - item.restLeft;
        if (phase === 'slide') {
          // Fija en vertical: solo se desliza en horizontal hacia la columna del original
          transform = `translateX(${dx * clamp01(1 - item.gap / TRAVEL_X)}px)`;
        } else if (phase === 'docked') {
          // Ya está en su sitio: se mueve con la página igual que el original
          transform = `translate(${dx}px, ${item.gap}px)`;
        }
      }

      this.#applyItem(item, phase !== 'released', transform);
    }

    if (active.length) bar.style.setProperty('--sticky-atc-bg-top', `${Math.max(0, bgTop - origin.top)}px`);

    // Cuando solo queda el botón, el fondo se funde a medida que el botón real se acerca
    if (this.#background) {
      const onlyButton = active.length === 1 && active[0]?.key === 'button';
      const fade = buttonPast ? 0 : clamp01(1 - buttonGap / BUTTON_FADE);
      this.#background.style.opacity = onlyButton ? String(1 - fade) : '';
    }
  }

  /**
   * Sin desplazamientos: cada elemento aparece en la barra (con un fundido suave) cuando la
   * barra tapa la mayor parte de su original, y desaparece en el acto cuando vuelve a asomar.
   * @param {Item[]} items
   * @param {{ buttonPast: boolean, buttonDocked: boolean, footerHit: boolean,
   *   origin: { top: number, left: number }, paddingTop: number, viewport: number }} state
   */
  #updateSimple(items, { buttonPast, buttonDocked, footerHit, origin, paddingTop, viewport }) {
    const bar = /** @type {HTMLElement} */ (this.#bar);
    const buttonItem = items.find((item) => item.key === 'button');
    const buttonShown = !buttonDocked;

    /** @type {Map<string, boolean>} */
    const shown = new Map();
    if (buttonItem) shown.set('button', buttonShown);

    // Borde superior de lo que la barra tapa ahora mismo
    let coverTop = buttonShown && buttonItem ? buttonItem.restTop - paddingTop : viewport;

    // De abajo arriba según la posición de los originales en la página
    const others = items
      .filter((item) => item.key !== 'button')
      .sort((a, b) => (b.target?.top ?? -Infinity) - (a.target?.top ?? -Infinity));

    for (const item of others) {
      let show;
      if (buttonPast) show = true;
      else if (!buttonShown) show = false;
      else if (!item.target) show = true; // sin original (p. ej. título de respaldo): sigue al botón
      else {
        // Aparece cuando el blanco sólido tapa al menos COVER_RATIO del original
        const stillVisible = item.target.height * (1 - COVER_RATIO);
        show = item.target.top + stillVisible >= coverTop + FADE_EDGE;
      }

      shown.set(item.key, show);
      if (show) coverTop = Math.min(coverTop, item.restTop - paddingTop);
    }

    const anyShown = Array.from(shown.values()).some(Boolean);
    const want = !footerHit && anyShown;
    const barWasVisible = this.#barVisible;
    if (want !== this.#barVisible) {
      // Aparece/desaparece en el sitio; solo desliza si es por el footer o por pasar el botón
      const slide = footerHit || buttonPast || this.#buttonWasPast;
      this.#setBarVisible(want, !slide);
    }

    // Los elementos que entran en la barra lo hacen con un fundido y una ligera subida
    // (salvo si la barra entera entra deslizando, que ya es su animación)
    const barSlidingIn = !barWasVisible && want && (buttonPast || this.#buttonWasPast);
    for (const item of items) {
      const isShown = Boolean(shown.get(item.key));
      const wasShown = Boolean(this.#shown.get(item.key)) && barWasVisible;
      this.#applyItem(item, isShown, '');

      const settle = this.#settle.get(item.key);
      this.#settle.delete(item.key);
      if (isShown && settle && item.clone && want) {
        // Venía desplazada de la primera bajada: vuelve a su sitio con suavidad, sin saltos
        item.clone.animate([{ transform: settle }, { transform: 'none' }], APPEAR);
      } else if (isShown && !wasShown && want && !barSlidingIn) {
        item.slot.animate(
          [
            { opacity: 0, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'none' },
          ],
          APPEAR
        );
      }
      this.#shown.set(item.key, isShown);
    }

    // El fondo crece hacia arriba con la misma curva; al retirarse lo hace en el acto para que
    // el original que vuelve a asomar no quede tapado
    if (anyShown && this.#background) {
      const top = Math.max(0, coverTop - origin.top);
      const growing = this.#simpleBgTop >= 0 && top < this.#simpleBgTop - 0.5 && barWasVisible && !barSlidingIn;
      this.#background.style.transition = growing ? `top ${APPEAR.duration}ms ${APPEAR.easing}` : 'none';
      bar.style.setProperty('--sticky-atc-bg-top', `${top}px`);
      this.#simpleBgTop = top;
    }
    if (this.#background) this.#background.style.opacity = '';
  }

  /**
   * Muestra u oculta la copia de un hueco y, a la inversa, el original.
   * @param {Item} item
   * @param {boolean} visibleInBar
   * @param {string} transform
   */
  #applyItem(item, visibleInBar, transform) {
    const { key, slot, clone, src } = item;
    slot.dataset.docked = String(!visibleInBar);
    if (clone && clone.style.transform !== transform) clone.style.transform = transform;

    // El botón real nunca se oculta (queda bajo la barra y recibe los clics reenviados)
    const covered = key !== 'button' && visibleInBar && this.#barVisible;
    if (src && src.hasAttribute(COVERED_ATTR) !== covered) src.toggleAttribute(COVERED_ATTR, covered);
  }

  /**
   * @param {boolean} visible
   * @param {boolean} [instant]
   */
  #setBarVisible(visible, instant = false) {
    const bar = this.#bar;
    if (!bar) return;
    this.#barVisible = visible;
    if (visible) this.#everShown = true;

    if (instant) {
      bar.setAttribute('data-instant', '');
      bar.dataset.visible = String(visible);
      void bar.offsetHeight;
      requestAnimationFrame(() => bar.removeAttribute('data-instant'));
    } else {
      bar.dataset.visible = String(visible);
    }
    bar.setAttribute('aria-hidden', String(!visible));
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
